import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ChevronDown, ChevronUp, Loader2, ShoppingCart, MapPin, Sparkles } from 'lucide-react'
import { useCart } from '@/lib/cart'
import { cn } from "@/lib/utils"
import type { StructuredItinerary, FlightResult, Hotel, ItineraryResponse, FlightAnalysis, FlightSearchParamsForAnalysis } from "@/types/travel"
import { StructuredItineraryView } from "./itinerary/StructuredItineraryView"
import { generateFlightAnalysis } from "@/lib/travel-api"

interface CollapsibleItineraryPreviewProps {
  isLoading: boolean
  itinerary: ItineraryResponse | null
  availableFlights?: FlightResult[]
  availableHotels?: Hotel[]
  onSelectItem?: (itemId: string, type: 'flight' | 'hotel') => void
  // VOY-18: Search params for flight analysis
  searchParams?: FlightSearchParamsForAnalysis
}

export function CollapsibleItineraryPreview({ 
  isLoading, 
  itinerary,
  availableFlights,
  availableHotels,
  onSelectItem,
  searchParams
}: CollapsibleItineraryPreviewProps) {
  const { cart, openCart } = useCart()
  const [isExpanded, setIsExpanded] = useState(true) // Expandido por defecto para destacar AI
  
  // VOY-18: Flight analysis state
  const [flightAnalysis, setFlightAnalysis] = useState<FlightAnalysis | null>(null)
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false)
  const analysisRequestedRef = useRef<string | null>(null) // Track which search params we've requested analysis for
  
  // Check if itinerary is structured or HTML based on the new format
  const isStructured = itinerary?.type === 'structured'
  const itineraryData = itinerary?.data
  
  // VOY-18: Fetch flight analysis when conditions are met
  useEffect(() => {
    // Create a unique key for this search
    const searchKey = searchParams 
      ? `${searchParams.origin}-${searchParams.destination}-${searchParams.departureDate}-${searchParams.returnDate || 'one-way'}`
      : null
    
    // Skip if:
    // - No search params
    // - No flights available
    // - Already requested for this search
    // - Currently loading
    if (!searchParams || !availableFlights?.length || isLoadingAnalysis) {
      return
    }
    
    // Skip if we already requested analysis for this exact search
    if (analysisRequestedRef.current === searchKey) {
      return
    }
    
    const fetchAnalysis = async () => {
      try {
        setIsLoadingAnalysis(true)
        analysisRequestedRef.current = searchKey
        
        console.log('[CollapsibleItineraryPreview] Fetching flight analysis for:', searchParams)
        
        const response = await generateFlightAnalysis(searchParams, {
          offers: availableFlights,
        })
        
        if (response.type === 'structured' && response.data) {
          setFlightAnalysis(response.data)
          console.log('[CollapsibleItineraryPreview] Flight analysis received:', response.data.recommendation.action)
        } else {
          console.warn('[CollapsibleItineraryPreview] Failed to get flight analysis:', response.error)
        }
      } catch (error) {
        console.error('[CollapsibleItineraryPreview] Error fetching flight analysis:', error)
      } finally {
        setIsLoadingAnalysis(false)
      }
    }
    
    fetchAnalysis()
  }, [searchParams, availableFlights, isLoadingAnalysis])
  
  // Reset analysis when search params change significantly
  useEffect(() => {
    const searchKey = searchParams 
      ? `${searchParams.origin}-${searchParams.destination}-${searchParams.departureDate}-${searchParams.returnDate || 'one-way'}`
      : null
    
    // If search changed, clear old analysis
    if (searchKey && analysisRequestedRef.current && analysisRequestedRef.current !== searchKey) {
      setFlightAnalysis(null)
      analysisRequestedRef.current = null
    }
  }, [searchParams])
  
  // Extract preview text from HTML content or structured itinerary
  const getPreviewText = (): string => {
    if (!itinerary || !itineraryData) return ""
    
    if (isStructured) {
      const structured = itineraryData as StructuredItinerary
      return structured.summary?.greeting || "Your personalized travel itinerary is ready"
    }
    
    const htmlContent = itineraryData as string
    // Strip code-fence fallback (```html ... ```) if present
    const fenced = htmlContent.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
    const cleanHtml = fenced ? fenced[1] : htmlContent;
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = cleanHtml
    const text = tempDiv.textContent || tempDiv.innerText || ""
    // Get first 250 characters for preview (increased from 150)
    return text.substring(0, 250) + (text.length > 250 ? "..." : "")
  }
  
  return (
    <div className="relative animate-in fade-in-0 slide-in-from-top-4 duration-700">
      <Card
        className={cn("itinerary-card w-full")}
      >
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer select-none hover:bg-gray-50/50 dark:hover:bg-zinc-800/50 transition-colors relative"
            >
              {/* AI Generated Badge */}
              <Badge 
                className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0 rounded-full px-3 py-1 shadow-lg"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                AI Generated
              </Badge>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary dark:text-blue-400" />
                  <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Suggested Itinerary</CardTitle>
                  {/* Mono meta line per Search+ design `.card-h .meta`. */}
                  <span className="itinerary-card-meta" aria-hidden="true">DRAFT · EDITABLE</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsExpanded(!isExpanded)
                  }}
                >
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
        
        <CardContent className={cn(
          "transition-all duration-300",
          !isExpanded && "pb-3"
        )}>
          {/* Collapsed Preview Content */}
          {!isExpanded && (
            <div className="space-y-2">
              {isLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Creating your personalized itinerary...
                  </p>
                </div>
              ) : itinerary ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">
                    {getPreviewText()}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsExpanded(true)
                    }}
                  >
                    Show more
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Search for flights to get a personalized travel itinerary
                  </p>
                  {cart.itemCount > 0 && (
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation()
                        openCart()
                      }}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                    >
                      <ShoppingCart className="h-3 w-3 mr-1" />
                      Cart ({cart.itemCount})
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
        
        {/* Expanded Content */}
        <CollapsibleContent className="transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <CardContent className="pt-0">
            <div className={cn(
              "max-h-[400px] overflow-y-auto",
              "scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700",
              "scrollbar-track-transparent pr-2"
            )}>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">
                      Creating your personalized itinerary...
                    </p>
                  </div>
                </div>
              ) : itinerary && itineraryData ? (
                isStructured ? (
                  <StructuredItineraryView
                    itinerary={itineraryData as StructuredItinerary}
                    availableFlights={availableFlights}
                    availableHotels={availableHotels}
                    onSelectItem={onSelectItem}
                    // VOY-18: Pass flight analysis props
                    searchParams={searchParams}
                    flightAnalysis={flightAnalysis}
                    isLoadingAnalysis={isLoadingAnalysis}
                  />
                ) : (
                  <div className="prose prose-sm max-w-none prose-headings:mb-2 prose-p:mb-2 dark:prose-invert">
                    {(() => {
                      const htmlRaw = itineraryData as string
                      const fenced = htmlRaw.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
                      const cleanHtml = fenced ? fenced[1] : htmlRaw;
                      return <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />
                    })()}
                  </div>
                )
              ) : (
                <div className="text-center py-8 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Search for flights to get a personalized travel itinerary
                  </p>
                  {cart.itemCount > 0 && (
                    <Button 
                      onClick={openCart}
                      className="w-full rounded-full"
                      size="sm"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      View Cart ({cart.itemCount})
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {/* Show collapse button when expanded and has content */}
            {itinerary && (
              <div className="mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full rounded-full"
                  onClick={() => setIsExpanded(false)}
                >
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Show less
                </Button>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
    </div>
  )
}